import { useCountryInfo } from 'legacy/lut/hooks/useCountryInfo'
import { useCountrySource } from 'legacy/lut/hooks/useCountrySource'
import { useCountryStateSource } from 'legacy/lut/hooks/useCountryStateSource'
import { memo, useCallback, useEffect, useMemo } from 'react'

import { Market, Region } from 'clickNgo/configs/geo'
import {
  HIERARCHY_TYPE_WITH_READONLY_LEGAL_INFO,
  PartnerFields,
  PartnerHierarchyType,
} from 'clickNgo/entities/partner/configs'
import { InfoBlock, InfoBlockType } from 'cng-common/components/infoBlock'
import { RenderWhen } from 'cng-common/components/renderWhen'
import {
  FormDropdown,
  FormInput,
  resetModifiedDataByField,
  useGetFormData,
  useGetFormDataField,
} from 'cng-common/features/form'
import { FieldWrapperWithChanges } from 'cng-common/features/form/components'
import { wasFieldModified } from 'cng-common/features/form/helpers'
import { Translation } from 'cng-common/features/translations'
import { useAppDispatch } from 'cng-common/hooks/useAppDispatch'
import { Checkbox } from 'core-components/library/checkbox'
import { Logic, Operator } from 'core-components/types'
import { ICountry } from 'core/classes/models/IReference'
import { MarketApi } from 'core/classes/services/api/MarketApi'
import { FieldGroup } from 'shared/components/fieldGroup/FieldGroup'

import { IPartnerFormBasic } from '../../../../models/form/basic'
import { IPartnerFormLegal } from '../../../../models/form/legal'
import { partnerBasicInfoTranslation } from '../../translation'

import { IAddressBlockProps } from './models'

import styles from './AddressBlock.module.scss'

const addressFields = [
  PartnerFields.addressLine1.field,
  PartnerFields.addressLine2.field,
  PartnerFields.addressLine3.field,
  PartnerFields.city.field,
  PartnerFields.zipCode.field,
  PartnerFields.state.field,
  PartnerFields.country.field,
]

const legalAddressFields = [
  PartnerFields.legalAddressLine1.field,
  PartnerFields.legalAddressLine2.field,
  PartnerFields.legalAddressLine3.field,
  PartnerFields.legalCity.field,
  PartnerFields.legalZipCode.field,
  PartnerFields.legalState.field,
  PartnerFields.legalCountry.field,
]

export const AddressBlock = memo<IAddressBlockProps>(
  ({
    country,
    addressLine1,
    addressLine2,
    addressLine3,
    city,
    modifiedValues,
    zipCode,
    state,
    isDisabled,
    isDisabledByRegion,
    formKeys,
    isReadOnly,
    isNewPartner,
    isAddressLine1Required,
    isAddressLine2Required,
    isAddressLine3Required,
    isCountryRequired,
    isCityRequired,
    isStateRequired,
    isZipCodeRequired,
    onAddressLine1Validate,
    onAddressLine2Validate,
    onAddressLine3Validate,
    onCountryValidate,
    onCityValidate,
    onStateValidate,
    onZipCodeValidate,
  }) => {
    /** -------------------- Variables ----------------------- */
    const dispatch = useAppDispatch()

    const [basicFormData, handleOnFormDataChange] = useGetFormData<IPartnerFormBasic>(
      formKeys.basic
    )
    const [legalFormData] = useGetFormData<IPartnerFormLegal>(formKeys.legal)

    const [doCopyBasicAddress, setDoCopyBasicAddress] = useGetFormDataField<boolean>(
      formKeys.settings,
      PartnerFields.doCopyBasicAddress.field
    )

    const isLegalFormDisabled = HIERARCHY_TYPE_WITH_READONLY_LEGAL_INFO.includes(
      basicFormData.formData.hierarchyType
    )

    const countrySource = useCountrySource({ isStatic: true })
    const countryInfo = useCountryInfo(basicFormData.formData.country)

    const stateSource = useCountryStateSource({
      countryName: basicFormData.formData.country || country,
      state: basicFormData.formData.state || state,
      isStatic: true,
    })

    const isCountryFieldDisabled = isDisabled || isDisabledByRegion || !isNewPartner
    const isStateFieldDisabled =
      (!basicFormData.formData.country && !country) || isDisabled || isDisabledByRegion

    const isAddressMatchingInfoVisible = useMemo(() => {
      return (
        doCopyBasicAddress &&
        addressFields.some((field) => basicFormData.modifiedFields.includes(field)) &&
        !isLegalFormDisabled
      )
    }, [basicFormData.modifiedFields, doCopyBasicAddress, isLegalFormDisabled])

    const addressMatchingInfo = useMemo(() => {
      const hasModifiedAddressFields = legalAddressFields.some((field) =>
        legalFormData.modifiedFields.includes(field)
      )
      const handleOnChange = (value: boolean) => {
        if (!value && hasModifiedAddressFields) {
          legalAddressFields.forEach((field) => {
            dispatch(resetModifiedDataByField({ formKey: formKeys.legal, field }))
          })
        }
        setDoCopyBasicAddress(value)
      }
      return (
        <div>
          <Translation
            text={partnerBasicInfoTranslation.notification.hasMatchingAddresses}
            span
            htmlAvailable
          />
          <Checkbox
            className={styles.checkbox}
            label={<Translation text={PartnerFields.doCopyBasicAddress.label} span htmlAvailable />}
            checked={doCopyBasicAddress}
            onChange={handleOnChange}
          />
        </div>
      )
    }, [
      dispatch,
      doCopyBasicAddress,
      formKeys.legal,
      legalFormData.modifiedFields,
      setDoCopyBasicAddress,
    ])

    /** -------------------- Handlers ----------------------- */

    const handleChangeCountry = useCallback(
      (_: string, countryInfo?: ICountry) => {
        if (countryInfo) {
          const market = countryInfo.market as Market
          const region = MarketApi.getRegion(market).Region as Region
          const countryCode = countryInfo.value

          handleOnFormDataChange({ field: PartnerFields.region.field, value: region })
          handleOnFormDataChange({ field: PartnerFields.market.field, value: market })
          handleOnFormDataChange({
            field: PartnerFields.countryCode.field,
            value: countryCode,
            ignoreModified: true,
          })
        } else {
          handleOnFormDataChange({ field: PartnerFields.region.field, value: Region.EMEA })
          handleOnFormDataChange({
            field: PartnerFields.market.field,
            value: Market.EMEA,
          })
        }
        handleOnFormDataChange({ field: PartnerFields.state.field, value: '' })
      },
      [handleOnFormDataChange]
    )

    /** -------------------- Effects ----------------------- */

    useEffect(() => {
      if (isNewPartner && countryInfo?.phoneCode && !basicFormData.formData.phone) {
        handleOnFormDataChange({
          field: PartnerFields.phoneCountryCode.field,
          value: countryInfo.phoneCode,
          doRevalidate: true,
        })
      }
    }, [countryInfo?.phoneCode, basicFormData.formData.phone, handleOnFormDataChange, isNewPartner])

    useEffect(() => {
      if (countryInfo?.value) {
        handleOnFormDataChange({
          field: PartnerFields.countryCode.field,
          value: countryInfo?.value,
          ignoreModified: true,
        })
      }
    }, [countryInfo?.value, handleOnFormDataChange])

    useEffect(() => {
      if (
        basicFormData.formData.hierarchyType === PartnerHierarchyType.RESELLER_NETWORK &&
        isNewPartner
      ) {
        countrySource.setFilter({
          logic: Logic.AND,
          filters: [
            {
              field: 'region',
              operator: Operator.NOTEQUAL,
              value: Region.AMS,
            },
          ],
        })
      } else {
        countrySource.setFilter({ logic: Logic.AND, filters: [] })
      }
    }, [countrySource, basicFormData.formData.hierarchyType, isNewPartner])

    return (
      <FieldGroup title={partnerBasicInfoTranslation.content.blockTitles.addressBlock}>
        <RenderWhen condition={isAddressMatchingInfoVisible}>
          <div style={{ gridColumn: '1 / -1' }}>
            <InfoBlock text={addressMatchingInfo} type={InfoBlockType.Warning} />
          </div>
        </RenderWhen>
        <FieldWrapperWithChanges
          hasChanges={wasFieldModified(modifiedValues, PartnerFields.addressLine1.externalField)}
        >
          <FormInput
            field={PartnerFields.addressLine1.field}
            label={PartnerFields.addressLine1.label}
            formKey={formKeys.basic}
            defaultValue={addressLine1}
            isDisabled={isDisabled}
            isReadOnly={isReadOnly}
            onValidate={onAddressLine1Validate}
            isRequired={isAddressLine1Required}
          />
        </FieldWrapperWithChanges>
        <FieldWrapperWithChanges
          hasChanges={wasFieldModified(modifiedValues, PartnerFields.addressLine2.externalField)}
        >
          <FormInput
            field={PartnerFields.addressLine2.field}
            label={PartnerFields.addressLine2.label}
            formKey={formKeys.basic}
            defaultValue={addressLine2}
            onValidate={onAddressLine2Validate}
            isDisabled={isDisabled}
            isReadOnly={isReadOnly}
            isRequired={isAddressLine2Required}
          />
        </FieldWrapperWithChanges>
        <FieldWrapperWithChanges
          hasChanges={wasFieldModified(modifiedValues, PartnerFields.addressLine3.externalField)}
        >
          <FormInput
            field={PartnerFields.addressLine3.field}
            label={PartnerFields.addressLine3.label}
            formKey={formKeys.basic}
            defaultValue={addressLine3}
            onValidate={onAddressLine3Validate}
            isDisabled={isDisabled}
            isReadOnly={isReadOnly}
            isRequired={isAddressLine3Required}
          />
        </FieldWrapperWithChanges>
        <FieldWrapperWithChanges
          hasChanges={wasFieldModified(modifiedValues, PartnerFields.country.externalField)}
        >
          <FormDropdown<ICountry>
            field={PartnerFields.country.field}
            label={PartnerFields.country.label}
            formKey={formKeys.basic}
            defaultValue={country}
            source={countrySource}
            caption="name"
            onChange={handleChangeCountry}
            onValidate={onCountryValidate}
            isDisabled={isCountryFieldDisabled}
            isReadOnly={isReadOnly}
            isRequired={isCountryRequired}
            isStatic
          />
        </FieldWrapperWithChanges>
        <FieldWrapperWithChanges
          hasChanges={wasFieldModified(modifiedValues, PartnerFields.state.externalField)}
        >
          <FormDropdown
            field={PartnerFields.state.field}
            label={PartnerFields.state.label}
            formKey={formKeys.basic}
            defaultValue={state}
            source={stateSource}
            caption="key"
            isDisabled={isStateFieldDisabled}
            isReadOnly={isReadOnly}
            isRequired={isStateRequired}
            onValidate={onStateValidate}
            isStatic
          />
        </FieldWrapperWithChanges>
        <FieldWrapperWithChanges
          hasChanges={wasFieldModified(modifiedValues, PartnerFields.city.externalField)}
        >
          <FormInput
            isDisabled={isDisabled || isDisabledByRegion}
            isReadOnly={isReadOnly}
            field={PartnerFields.city.field}
            label={PartnerFields.city.label}
            formKey={formKeys.basic}
            defaultValue={city}
            onValidate={onCityValidate}
            isRequired={isCityRequired}
          />
        </FieldWrapperWithChanges>
        <FieldWrapperWithChanges
          hasChanges={wasFieldModified(modifiedValues, PartnerFields.zipCode.externalField)}
        >
          <FormInput
            field={PartnerFields.zipCode.field}
            label={PartnerFields.zipCode.label}
            formKey={formKeys.basic}
            defaultValue={zipCode}
            onValidate={onZipCodeValidate}
            isDisabled={isDisabled}
            isReadOnly={isReadOnly}
            isRequired={isZipCodeRequired}
          />
        </FieldWrapperWithChanges>
      </FieldGroup>
    )
  }
)

AddressBlock.displayName = 'AddressBlock'
